import React from "react";
import { FaCalendarAlt, FaMapMarkerAlt, FaTrophy, FaUsers } from "react-icons/fa";

import { SectionCard } from "components/admin/ui";
import { SkeletonBlock } from "components/admin/loading";
import { formatDateTime } from "utils/admin";

import {
  SchoolMatchWeekHighlightAccent,
  SchoolMatchWeekHighlightCard,
  SchoolMatchWeekHighlightEyebrow,
  SchoolMatchWeekHighlightHeader,
  SchoolMatchWeekHighlightIconWrap,
  SchoolMatchWeekHighlightMeta,
  SchoolMatchWeekHighlightPending,
  SchoolMatchWeekHighlightSkeletonAccent,
  SchoolMatchWeekHighlightSkeletonCard,
  SchoolMatchWeekHighlightValue,
  SchoolMatchWeekHighlightsGrid,
  SchoolMatchWeekHighlightsSkeletonGrid,
  SchoolMatchWeekHighlightsFooter,
  SchoolMatchWeekHighlightVariant,
} from "./styles/school-match-week-highlights.styles";

type SchoolMatchWeekHighlightsProps = {
  children?: React.ReactNode;
  isLoading?: boolean;
  matchWeek?: {
    scheduled_at?: string;
    school_match_id?: string | number;
    venue_name?: string;
  } | null;
  onSelectMatch?: (matchId: string) => void;
  onSelectPlayer?: (playerId: string) => void;
  onSelectTeam?: (teamId: string) => void;
  showTeamOfTheWeek?: boolean;
  subtitle?: string;
  title?: string;
  weeklyHighlights?: {
    player_of_the_week?: {
      player_id?: string | number;
      player_name?: string;
    };
    team_of_the_week?: {
      school_team_id?: string | number;
      school_team_name?: string;
      team_id?: string | number;
      team_name?: string;
    };
  } | null;
};

const HIGHLIGHT_ICONS: Record<SchoolMatchWeekHighlightVariant, React.ReactNode> = {
  player: <FaTrophy size={14} />,
  team: <FaUsers size={14} />,
  match: <FaCalendarAlt size={14} />,
  venue: <FaMapMarkerAlt size={14} />,
};

const renderHighlightValue = (value?: string | null, pendingLabel = "Pending") =>
  value ? (
    <SchoolMatchWeekHighlightValue>{value}</SchoolMatchWeekHighlightValue>
  ) : (
    <SchoolMatchWeekHighlightValue>
      <SchoolMatchWeekHighlightPending>{pendingLabel}</SchoolMatchWeekHighlightPending>
    </SchoolMatchWeekHighlightValue>
  );

type HighlightTileProps = {
  helper?: string;
  label: string;
  onClick?: () => void;
  value?: string | null;
  variant: SchoolMatchWeekHighlightVariant;
  pendingLabel?: string;
};

const HighlightTile: React.FC<HighlightTileProps> = ({
  helper,
  label,
  onClick,
  pendingLabel,
  value,
  variant,
}) => {
  const content = (
    <>
      <SchoolMatchWeekHighlightAccent $variant={variant} />
      <SchoolMatchWeekHighlightHeader>
        <SchoolMatchWeekHighlightEyebrow $variant={variant}>{label}</SchoolMatchWeekHighlightEyebrow>
        <SchoolMatchWeekHighlightIconWrap $variant={variant}>
          {HIGHLIGHT_ICONS[variant]}
        </SchoolMatchWeekHighlightIconWrap>
      </SchoolMatchWeekHighlightHeader>
      {renderHighlightValue(value, pendingLabel)}
      {helper ? <SchoolMatchWeekHighlightMeta>{helper}</SchoolMatchWeekHighlightMeta> : null}
    </>
  );

  if (onClick) {
    return (
      <SchoolMatchWeekHighlightCard
        $interactive
        $variant={variant}
        as="button"
        onClick={onClick}
        type="button"
      >
        {content}
      </SchoolMatchWeekHighlightCard>
    );
  }

  return <SchoolMatchWeekHighlightCard $variant={variant}>{content}</SchoolMatchWeekHighlightCard>;
};

export const SchoolMatchWeekHighlightsSkeleton: React.FC<{
  showTeamOfTheWeek?: boolean;
  subtitle?: string;
  title?: string;
}> = ({
  showTeamOfTheWeek = true,
  subtitle = "Weekly highlights for the selected school.",
  title = "Match Week",
}) => (
  <SectionCard subtitle={subtitle} title={title}>
    <SchoolMatchWeekHighlightsSkeletonGrid aria-label="Loading match week highlights" role="status">
      {Array.from({ length: showTeamOfTheWeek ? 4 : 3 }).map((_, index) => (
        <SchoolMatchWeekHighlightSkeletonCard key={index}>
          <SchoolMatchWeekHighlightSkeletonAccent />
          <div className="mb-3 flex items-start justify-between gap-3">
            <SkeletonBlock className="h-3 w-24 rounded-full" />
            <SkeletonBlock className="h-9 w-9 rounded-[0.9rem]" />
          </div>
          <SkeletonBlock className="h-7 w-3/4 rounded-[14px]" />
          <SkeletonBlock className="mt-3 h-3 w-1/2 rounded-full" />
        </SchoolMatchWeekHighlightSkeletonCard>
      ))}
    </SchoolMatchWeekHighlightsSkeletonGrid>
  </SectionCard>
);

export const SchoolMatchWeekHighlights: React.FC<SchoolMatchWeekHighlightsProps> = ({
  children,
  isLoading = false,
  matchWeek,
  onSelectMatch,
  onSelectPlayer,
  onSelectTeam,
  showTeamOfTheWeek = true,
  subtitle = "Weekly highlights for the selected school.",
  title = "Match Week",
  weeklyHighlights,
}) => {
  if (isLoading) {
    return (
      <SchoolMatchWeekHighlightsSkeleton
        showTeamOfTheWeek={showTeamOfTheWeek}
        subtitle={subtitle}
        title={title}
      />
    );
  }

  const playerOfTheWeek = weeklyHighlights?.player_of_the_week;
  const playerOfTheWeekId = playerOfTheWeek?.player_id ? String(playerOfTheWeek.player_id) : "";
  const teamOfTheWeekRecord = weeklyHighlights?.team_of_the_week;
  const teamOfTheWeek =
    teamOfTheWeekRecord?.team_name || teamOfTheWeekRecord?.school_team_name || "";
  const teamOfTheWeekId = teamOfTheWeekRecord?.school_team_id || teamOfTheWeekRecord?.team_id
    ? String(teamOfTheWeekRecord.school_team_id || teamOfTheWeekRecord.team_id)
    : "";
  const nextMatchAt = matchWeek?.scheduled_at ? formatDateTime(matchWeek.scheduled_at) : "";
  const nextMatchId = matchWeek?.school_match_id ? String(matchWeek.school_match_id) : "";
  const venueName = matchWeek?.venue_name || "";

  return (
    <SectionCard subtitle={subtitle} title={title}>
      <SchoolMatchWeekHighlightsGrid>
        <HighlightTile
          helper={playerOfTheWeekId && onSelectPlayer ? "Open player profile" : undefined}
          label="Player of the Week"
          onClick={
            playerOfTheWeekId && onSelectPlayer
              ? () => onSelectPlayer(playerOfTheWeekId)
              : undefined
          }
          value={playerOfTheWeek?.player_name}
          variant="player"
        />
        {showTeamOfTheWeek ? (
          <HighlightTile
            helper={teamOfTheWeekId && onSelectTeam ? "Open team profile" : undefined}
            label="Team of the Week"
            onClick={
              teamOfTheWeekId && onSelectTeam ? () => onSelectTeam(teamOfTheWeekId) : undefined
            }
            value={teamOfTheWeek}
            variant="team"
          />
        ) : null}
        <HighlightTile
          helper={
            nextMatchId && onSelectMatch
              ? "Open match hub"
              : nextMatchAt
              ? "Next scheduled fixture in scope"
              : undefined
          }
          label="Next Match"
          onClick={
            nextMatchId && onSelectMatch ? () => onSelectMatch(nextMatchId) : undefined
          }
          pendingLabel="No fixture scheduled"
          value={nextMatchAt}
          variant="match"
        />
        <HighlightTile
          label="Venue"
          pendingLabel="Venue pending"
          value={venueName}
          variant="venue"
        />
      </SchoolMatchWeekHighlightsGrid>
      {children ? <SchoolMatchWeekHighlightsFooter>{children}</SchoolMatchWeekHighlightsFooter> : null}
    </SectionCard>
  );
};
